import React, { useEffect, useState } from 'react';
import {
  loadDemoLatestReport,
  loadDemoOutputs,
  loadDemos,
  signedDemoBundle,
  signedDemoOutput,
  signedDemoReport,
  webserverJobStatus,
} from './xomics-webserver-client';
import './webserver-shared.css';

function openSigned(body) {
  if (!body?.downloadUrl) throw new Error('This file is not available for download');
  window.open(body.downloadUrl, '_blank', 'noopener,noreferrer');
}

function DemoCard({ serviceCode, demo }) {
  const [outputs, setOutputs] = useState(null);
  const [report, setReport] = useState(undefined);
  const [error, setError] = useState('');
  const status = webserverJobStatus(demo.job, null);
  const finished = status === 'completed';

  useEffect(() => {
    if (!finished) return undefined;
    let cancelled = false;
    loadDemoLatestReport(serviceCode, demo.uuid)
      .then((latest) => { if (!cancelled) setReport(latest); })
      .catch(() => { if (!cancelled) setReport(null); });
    return () => { cancelled = true; };
  }, [serviceCode, demo.uuid, finished]);

  const run = async (action) => {
    setError('');
    try {
      await action();
    } catch (actionError) {
      setError(actionError.message || 'The request failed');
    }
  };

  return (
    <div className="WSX-demo-card" data-testid="wsx-demo-card">
      <div className="WSX-demo-head">
        <span className="WSX-demo-badge">DEMO</span>
        <span className="WSX-demo-title">{demo.title}</span>
      </div>
      <div className="WSX-muted">
        {demo.job?.display_name || demo.job?.pipeline || 'Analysis'}
        {demo.job?.code_version ? ` ${demo.job.code_version}` : ''} · {status}
      </div>
      {finished && (
        <div className="WSX-row">
          <button
            type="button"
            className="WSX-button"
            onClick={() => run(async () => setOutputs(outputs ? null : await loadDemoOutputs(serviceCode, demo.uuid)))}
          >
            <i className="fas fa-folder-open" /> {outputs ? 'Hide files' : 'Files'}
          </button>
          <button
            type="button"
            className="WSX-button"
            onClick={() => run(async () => openSigned(await signedDemoBundle(serviceCode, demo.uuid)))}
          >
            <i className="fas fa-download" /> Download all
          </button>
          {report && String(report.status).toLowerCase() === 'done' && (
            <button
              type="button"
              className="WSX-button"
              onClick={() => run(async () => openSigned(await signedDemoReport(serviceCode, demo.uuid, report.uuid)))}
            >
              <i className="fas fa-file-pdf" /> Report
            </button>
          )}
        </div>
      )}
      {outputs && (
        <ul className="WSX-demo-files">
          {outputs.length === 0 && <li className="WSX-muted">No result files.</li>}
          {outputs.map((output) => (
            <li key={output.id}>
              <button
                type="button"
                className="WSX-link"
                onClick={() => run(async () => openSigned(await signedDemoOutput(serviceCode, demo.uuid, output.id)))}
              >
                {output.filename}
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && <p className="WSX-error">{error}</p>}
    </div>
  );
}

// Demo analyses any visitor may open for this webserver, from its public demo
// registry.
export default function WebserverDemoSection({ serviceCode, title = 'Demo analyses' }) {
  const [demos, setDemos] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadDemos(serviceCode)
      .then((list) => { if (!cancelled) setDemos(list); })
      .catch((loadError) => { if (!cancelled) setError(loadError.message || 'Demo analyses could not be loaded'); });
    return () => { cancelled = true; };
  }, [serviceCode]);

  return (
    <section className="WSX-panel WSX-demo-section" data-testid="wsx-demo-section">
      <div className="WSX-panel-title"><i className="fas fa-flask" /> {title}</div>
      {error && <p className="WSX-error">Demo analyses could not be loaded: {error}</p>}
      {!error && demos === null && <p className="WSX-muted">Loading demo analyses...</p>}
      {!error && demos?.length === 0 && <p className="WSX-muted">No demo analyses are published yet.</p>}
      <div className="WSX-demo-list">
        {(demos || []).map((demo) => <DemoCard key={demo.uuid} serviceCode={serviceCode} demo={demo} />)}
      </div>
    </section>
  );
}
